import Vue from 'vue';
import store from '@/store';
import Router from 'vue-router';

import { getCurrent } from '@/services/AuthService';
import Layout from './views/Layout.vue';
import PageLogin from './views/PageLogin.vue';
import componentsRouter from './components.router';
import PageCreateCampaign from './views/PageCreateCampaign.vue';
import PageViewCampaign from './views/PageViewCampaign.vue';
import PageAccessDenied from './views/PageAccessDenied.vue';
import PageEditBots from './views/PageEditBots.vue';
import { Campaign, Integration } from './models';
import PermissionService from './services/PermissionService';
import CampaignService from './services/CampaignService';
import AuthService from './services/AuthService';

Vue.use(Router);

let devRoutes: any = [];

if (process.env.NODE_ENV === 'development') {
  devRoutes = [...componentsRouter];
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    ...devRoutes,
    {
      path: '/login',
      name: 'login',
      component: PageLogin,
      meta: {
        requiresGuest: true,
      },
    },
    {
      path: '/',
      redirect: '/campaign',
      component: Layout,
      children: [{
        path: '/access_denied',
        component: PageAccessDenied,
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageDashboard.vue'),
        meta: {
          requiresAdmin: true,
        },
      },
      {
        path: 'chat',
        name: 'chatPage',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageChat.vue'),
      },
      {
        path: 'chat/team/:entityId',
        name: 'chatTabTeam',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageChat.vue'),
        children: [{
          name: 'selectedChatTeam',
          path: ':id',
          component: () => import(/* webpackChunckName: "emp" */ './views/PageMessages.vue'),
        }],
      },
      {
        path: 'chat/user/:entityId',
        name: 'chatTabUser',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageChat.vue'),
        children: [{
          name: 'selectedChatUser',
          path: ':id',
          component: () => import(/* webpackChunckName: "emp" */ './views/PageMessages.vue'),
        }],
      },
      {
        path: 'chat/:tab',
        name: 'chatTab',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageChat.vue'),
        children: [{
          name: 'selectedChat',
          path: ':id',
          component: () => import(/* webpackChunckName: "emp" */ './views/PageMessages.vue'),
        }],
      },
      {
        path: 'orders',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageOrders.vue'),
      },
      {
        path: 'products',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageProducts.vue'),
      },
      {
        path: 'clients',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageClients.vue'),
      },
      {
        path: 'collections',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageCollections.vue'),
      },
      {
        path: 'customer-support',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageCustomerSupports.vue'),
      },
      {
        path: 'settings',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageSettings.vue'),
      },
      {
        path: 'analytics',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageAnalytics.vue'),
      },
      {
        path: 'bots',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageBots.vue'),
        children: [
          {
            name: 'bots',
            path: '/',
            component: () => import(/* webpackChunckName: "emp" */ './views/PageListBots.vue'),
          },
          {
            name: 'botEdit',
            path: ':id',
            component: PageEditBots,
            props: (route: any) => ({
              bot: store.state.bot.bots.find((b: Integration) => b.objectId === route.params.id),
            }),
          },
        ],
        meta: {
          requiresAdmin: true,
        },
      },
      {
        path: 'campaign',
        component: () => import(/* webpackChunkName: "emp" */ './views/PageCampaign.vue'),
        children: [
          {
            name: 'campaignCreate',
            path: 'create',
            component: PageCreateCampaign,
            props: (route: any) => {
              const found = store.state.campaign.campaign || store.state.campaign.campaigns.find(
                (c: Campaign) => c.objectId === route.query.id,
              ) || {};
              return {
                campaign: CampaignService.extractDuplicatableFields(found as any),
              };
            },
          },
          {
            name: 'campaignEdit',
            path: ':id/edit',
            component: PageCreateCampaign,
            props: (route: any) => ({
              campaign: store.state.campaign.campaigns.find((c: Campaign) => c.objectId === route.params.id),
            }),
          },
          {
            name: 'campaignView',
            path: ':id',
            component: PageViewCampaign,
          },
          {
            name: 'campaignList',
            path: '/',
            component: () => import(/* webpackChunckName: "emp" */ './views/PageListCampaign.vue'),
          },
        ],
        meta: {
          requiresWAB: true,
          requiresSMSIntegration: true,
        },
      },
      ],
      meta: {
        requiresAuth: true,
      },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  let nextRoute = null;
  if (to.query && to.query.sessionToken) {
    store.dispatch('logout');
    const user = await getCurrent(to.query.sessionToken as string);
    store.commit('setCurrentUser', user);
    await AuthService.afterLogin();
    nextRoute = { path: to.path };
  }
  // ====== Authentication checks =====
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!store.state.auth.currentUser) {
      nextRoute = {
        path: '/login',
        query: {
          redirect: to.fullPath,
        },
      };
    }
  } else if (to.matched.some((record) => record.meta.requiresGuest)) {
    if (store.state.auth.currentUser) {
      nextRoute = { path: '/campaign' };
    }
  }
  // ====== Permission checks =====
  if (to.matched.some((record) => record.meta.requiresAdmin)) {
    if (!PermissionService.isAdmin()) {
      nextRoute = { path: '/access_denied?cause=no_admin' };
    }
  }
  if (to.matched.some((record) => record.meta.requiresWAB)) {
    if (!PermissionService.isWABEnabled()) {
      nextRoute = { path: '/access_denied?cause=no_wab_enabled' };
    }
  }
  if (to.matched.some((record) => record.meta.requiresSMSIntegration)) {
    if (!await PermissionService.isIntegrationEnabled('sms')) {
      nextRoute = { path: '/access_denied?cause=no_sms_enabled' };
    }
  }
  if (!nextRoute) {
    next();
  } else {
    next(nextRoute);
  }
});

export default router;
